import React from 'react'
import type { Maybe } from '@adc/sdk/types'

/**
 * Anything with a `formatted_address` field can be passed to <Address /> or
 * Address.format(). This matches the shape emitted by the graph for Listing,
 * Venue, Summary, Address, etc. — the field is a tuple of address lines
 * (e.g. ['123 Main St', 'City, ST 12345']), though some types return a plain
 * string, so both are accepted.
 */
export interface HasFormattedAddress {
  formatted_address?: Maybe<ReadonlyArray<Maybe<string>>> | Maybe<string>
}

export interface AddressProps {
  /** Any graph object carrying a `formatted_address` field. */
  source: HasFormattedAddress | null | undefined
  /** Separator used when joining address lines. Defaults to ', '. */
  separator?: string
  /** Render only a single line by index (e.g. 0 = street, 1 = city/state/zip). */
  line?: number
  /** Element to render as. Defaults to 'span'. */
  as?: 'span' | 'div' | 'p'
  className?: string
  elmId?: string
}

type AddressComponent = React.FC<AddressProps> & {
  /**
   * Normalizes a `formatted_address` into positional, trimmed lines. Positions
   * are preserved (empty entries become '') so callers relying on a fixed
   * layout — e.g. line 0 = street, line 1 = city/state/zip — stay aligned.
   * Returns [] only when the source has no address at all.
   */
  toLines: (source: HasFormattedAddress | null | undefined) => string[]
  /**
   * Pure helper for non-JSX contexts (meta tags, analytics, email bodies).
   * Joins the non-empty lines, so empty parts never produce orphan separators
   * (e.g. ' , City, ST'). Returns '' when there is nothing to show.
   */
  format: (
    source: HasFormattedAddress | null | undefined,
    separator?: string
  ) => string
}

/**
 * Null-safe address renderer. Wraps the repeated join / index / null-check
 * logic scattered across the PDP. Renders nothing when there is no address.
 *
 * Non-JSX callers use the static helpers: Address.format() / Address.toLines().
 */
export const Address: AddressComponent = ({
  source,
  separator = ', ',
  line,
  as: Tag = 'span',
  className,
  elmId,
}: AddressProps) => {
  const text =
    typeof line === 'number'
      ? Address.toLines(source)[line] ?? ''
      : Address.format(source, separator)

  if (!text) return null

  return (
    <Tag className={className} data-elm-id={elmId}>
      {text}
    </Tag>
  )
}

Address.toLines = source => {
  const value = source?.formatted_address
  if (!value) return []

  const parts: ReadonlyArray<Maybe<string>> =
    typeof value === 'string' ? [value] : value

  return parts.map(part => part?.trim() ?? '')
}

Address.format = (source, separator = ', ') =>
  Address.toLines(source).filter(Boolean).join(separator)

export default Address
